/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { useShellStore } from './shellStore';
import { useMediaMax } from './useCondense';
import { useFitRail } from './useFitRail';
import { Ico } from './ShellIcons';
import FilterField, { OptionRows } from './FilterField';
import MoreFilters from './MoreFilters';
import { Seg, Chips, Toggle } from './InlineFields';
import { LineToggle, YearTabs } from './RailAxes';
import r from './rail.module.css';

// ============================================================
// Rail — the third row of Shell v3 (CLB-064, prototype v2 approved by David
// 11.10): a tinted row under the portal bar with THREE labelled groups that
// never blur together —
//   SCOPE   (amber)  where / when you are looking: line, years, period, lane
//   FILTERS (grey)   the narrowing fields + "All filters"
//   RESULT  (dashed) what you are looking at: rows · models · amount · Clear
// The portal bar above keeps identity, status, search, view tools and the
// primary action. The rail measures itself and collapses least important
// first (useFitRail) — nothing wraps, nothing is cut. On a phone it becomes
// two chips (Scope · Filters) that open a bottom sheet with the full groups.
//
// cfg.rail = { tall?, scope: { label, icon, summary, fields }, filters: { label,
//   fields, onClearAll }, result: { text, title, onClear } }
// field types: 'line' · 'years' · 'seg' · 'chips' · 'toggle' · 'tag' (a read-only value) · 'custom' ·
// 'divider' · (default) list. `axis` names a field inside its group.
// ============================================================

export const activeCount = (fields) => (fields || []).reduce((n, f) => {
  if (f.type === 'toggle') return n + (f.value && f.countsAsFilter !== false ? 1 : 0);
  if (f.type === 'seg' || f.type === 'chips') return n + (f.defaultValue !== undefined && f.value != null && f.value !== f.defaultValue && f.countsAsFilter !== false ? 1 : 0);
  if (f.type === 'custom' || f.type === 'divider' || f.type === 'line' || f.type === 'years' || f.type === 'tag') return n;
  return n + (f.value && f.value.size ? 1 : 0);
}, 0);

function Item({ f, prio, open, toggleOpen, closeAll }) {
  if (f.type === 'divider') return <span className={r.gdiv} />;
  const k = `rail:${f.key}`;
  let body;
  if (f.type === 'line') body = <LineToggle field={f} />;
  else if (f.type === 'years') body = <YearTabs field={f} />;
  else if (f.type === 'seg') body = <Seg field={{ ...f, tag: undefined }} />;
  else if (f.type === 'chips') body = <Chips field={f} />;
  else if (f.type === 'toggle') body = <Toggle field={f} />;
  else if (f.type === 'tag') body = <span className={`${r.tag} ${f.muted ? r.tagMuted : ''}`} data-test={f.testId}>{f.text}</span>;
  else if (f.type === 'custom') body = f.render();
  else return <FilterField field={{ ...f, priority: f.priority || prio }} open={open === k} onToggle={() => toggleOpen(k)} onClose={closeAll} />;
  return (
    <div className={r.ax} data-rail-item={f.type} title={f.title}>
      {f.axis ? <span className={r.axl}>{f.axis}</span> : null}
      {body}
    </div>
  );
}

// In the phone sheet a list filter is its option list, in place — a popover would
// take the shell's one open key and close the sheet it was opened from.
function SheetItem({ f, ...rest }) {
  if (f.type && f.type !== 'list') return <Item f={f} {...rest} />;
  const n = f.value && f.value.size ? f.value.size : 0;
  return (
    <section className={r.sblock} data-sheet-list={f.key}>
      <div className={r.shead}><b>{f.label || f.key}</b>{n ? <span>{n} on</span> : null}</div>
      <OptionRows field={{ ...f, searchable: f.searchable != null ? f.searchable : f.options.length > 8 }} onChange={f.onChange} />
    </section>
  );
}

function Group({ kind, g, children }) {
  return (
    <div className={`${r.grp} ${r[kind]}`} data-rail-group={kind}>
      <span className={r.gl}><Ico name={g.icon || (kind === 'filt' ? 'filter' : 'layers')} />{g.iconOnly ? null : <span>{g.label}</span>}</span>
      {children}
    </div>
  );
}

export default function Rail({ cfg }) {
  const ref = useRef(null);
  const open = useShellStore((s) => s.open);
  const toggleOpen = useShellStore((s) => s.toggleOpen);
  const closeAll = useShellStore((s) => s.closeAll);
  const setChrome = useShellStore((s) => s.setChrome);
  const phone = useMediaMax(599);
  const height = phone ? 44 : cfg.tall ? 54 : 40;
  useEffect(() => { setChrome({ railH: height }); }, [height, setChrome]);
  useEffect(() => () => setChrome({ railH: 0 }), [setChrome]);
  useFitRail(ref, r, phone);
  // Clear all hands back an undo: for six seconds the Clear button reads "Undo".
  // An undo belongs to the result that made it (result.undoKey): leaving that screen drops it.
  const [undo, setUndo] = useState(null);
  const ukey = cfg.result ? cfg.result.undoKey : undefined;
  useEffect(() => { setUndo((u) => (u && u.key === ukey ? u : null)); }, [ukey]);
  useEffect(() => { if (!undo) return undefined; const t = setTimeout(() => setUndo(null), 6000); return () => clearTimeout(t); }, [undo]);
  const doClear = () => { const back = cfg.result.onClear(); if (typeof back === 'function') setUndo({ key: cfg.result.undoKey, back }); };

  const scope = cfg.scope;
  const filters = cfg.filters;
  const fFields = (filters && filters.fields) || [];
  const listable = fFields.filter((f) => f.type !== 'custom' && f.type !== 'divider' && f.type !== 'tag');
  const nOn = activeCount(fFields);
  // a list field's priority is its order (rF hides the highest first) unless it set its own
  const items = (fields) => { let n = 0; return (fields || []).map((f, i) => <Item key={f.key || `d${i}`} f={f} prio={(!f.type || f.type === 'list') ? (n += 1) : 0} open={open} toggleOpen={toggleOpen} closeAll={closeAll} />); };
  const result = cfg.result ? (
    <div className={`${r.grp} ${r.res}`} data-rail-group="res" title={cfg.result.title}>
      {cfg.bare ? null : <span className={r.gl}><span>Result</span></span>}
      <span className={r.rv}>{cfg.result.text}</span>
      {undo && undo.key === cfg.result.undoKey && ukey ? (
        <button type="button" className={r.clr} data-test="rail-undo" onClick={(e) => { e.stopPropagation(); const back = undo.back; setUndo(null); back(); }} title="Put the filters back"><Ico name="undo" /><span>Undo</span></button>
      ) : cfg.result.onClear && (nOn > 0 || cfg.result.clearable) ? (
        <button type="button" className={r.clr} onClick={(e) => { e.stopPropagation(); doClear(); }} title={cfg.result.clearLabel ? cfg.result.clearLabel : 'Clear every filter'}><Ico name="x" /><span>{cfg.result.clearLabel || 'Clear'}</span></button>
      ) : null}
    </div>
  ) : null;

  if (phone) {
    const sheet = open === 'rail:sheet:scope' ? 'scope' : open === 'rail:sheet:filters' ? 'filters' : null;
    return (
      <div className={`${r.rail} ${r.phone} ${sheet ? r.lift : ''}`} data-shell-rail data-test="shell-rail">
        {scope ? <button type="button" className={`${r.pchip} ${r.pscope}`} onClick={(e) => { e.stopPropagation(); toggleOpen('rail:sheet:scope'); }}><Ico name={scope.icon || 'layers'} /><span>{scope.summary || scope.label}</span><Ico name="chev" className={r.chev} /></button> : null}
        {filters && fFields.length ? <button type="button" className={r.pchip} onClick={(e) => { e.stopPropagation(); toggleOpen('rail:sheet:filters'); }}><Ico name="filter" /><span>{filters.label || 'Filters'}</span>{nOn ? <b className={r.pn}>{nOn}</b> : null}<Ico name="chev" className={r.chev} /></button> : null}
        {cfg.result ? <span className={r.prv}>{cfg.result.short || cfg.result.text}</span> : null}
        {sheet ? (
          <>
            <div className={r.scrim} onClick={closeAll} />
            <div className={r.sheet} data-shell-pop onClick={(e) => e.stopPropagation()} role="dialog" aria-label={sheet === 'scope' ? scope.label : filters.label}>
              <div className={r.sheetHead}><b>{sheet === 'scope' ? scope.label : (filters.label || 'Filters')}</b><button type="button" onClick={closeAll} aria-label="Close"><Ico name="x" /></button></div>
              <div className={r.sheetBody}>{(sheet === 'scope' ? scope.fields : fFields).map((f, i) => <SheetItem key={f.key || `d${i}`} f={f} prio={0} open={open} toggleOpen={toggleOpen} closeAll={closeAll} />)}</div>
              {sheet === 'filters' && filters.onClearAll ? <div className={r.sheetFoot}><button type="button" onClick={() => filters.onClearAll()}>Clear all</button><button type="button" className={r.go} onClick={closeAll}>Done</button></div> : null}
            </div>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div ref={ref} className={`${r.rail} ${cfg.tall ? r.tall : ''} ${cfg.bare ? r.bare : ''}`} data-shell-rail data-test="shell-rail">
      {scope && !scope.phoneOnly ? <Group kind="scope" g={scope}>{items(scope.fields)}</Group> : null}
      {filters && fFields.length ? (
        <Group kind="filt" g={{ label: filters.label || 'Filters', icon: filters.icon || 'filter', iconOnly: filters.iconOnly }}>
          {items(fFields)}
          {listable.length && filters.more !== false ? (
            <MoreFilters label="All filters" fields={listable} open={open === 'rail:more'} onToggle={() => toggleOpen('rail:more')} onClose={closeAll}
              onClearAll={() => { filters.onClearAll && filters.onClearAll(); closeAll(); }} activeCount={nOn} />
          ) : null}
        </Group>
      ) : null}
      {result}
    </div>
  );
}
